import React, { useState, useEffect, useLayoutEffect } from 'react';
import Search from '@ingka/search';
import styled from 'styled-components';
import useTranslation from '../hooks/useTranslation';
import Loading, { LoadingBall } from '@ingka/loading';
import { useCart } from '../contexts/Cart';
import { useMedia } from '../hooks/useMedia';
import { DisplaySearchItems } from './DisplaySearchItems';
import { Onboarding } from './Onboarding';
import { SparePart } from '../interfaces/Item';
import { SelectedItem } from './ItemDisplay';
import { useSearch } from '../hooks/useSearch';
import { getURLParameters } from '../helpers/url';
import greenDayImage from '../images/onboarding/greenday.png';
import AspectRatioImage from '@ingka/aspect-ratio-image';
import TMarkUp from './TMarkUp';
import { sparePartService } from '../services';
import { removeEmojis } from '../utils/emojiFilter';

type HTMLElementEvent<T extends HTMLElement> = Event & {
  target: T;
};

/* ---------------- STYLES ---------------- */

const FlexContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 48px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const StyledSearchField = styled.div`
  width: 480px;
  max-width: 100%;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SparePartsDescription = styled.p`
  max-width: 600px;
  margin: 8px 0 24px 0;
  line-height: 1.5;
  color: #333;
`;

const OnDesktopImage = styled.div`
  max-width: 500px;
  width: 100%;
  align-self: flex-start;
  margin-inline-start: auto;

  @media (max-width: 900px) {
    display: none;
  }
`;

const OnTabletImage = styled.div`
  margin-bottom: 46px;
  margin-top: 24px;
  max-width: 578px;
  width: 100%;
`;

const StyledSearch = styled(Search)`
  background-color: #f3f3f3;
  min-width: 200px;
  margin-top: 12px;

  button.search__clear {
    position: absolute;
  }
`;

const DisplaySearchItemsWrapper = styled.ul`
  list-style: none;
  max-width: 800px;
  padding: 0;
`;

const SearchModeContainer = styled.div`
  width: 100%;
  max-width: 800px; /* ensures alignment with results */
  margin-bottom: 24px;
`;

/* ---------------- COMPONENT ---------------- */

const SearchResults = (): JSX.Element => {
  const { t } = useTranslation();
  const CartContext = useCart();
  const refInput = React.useRef<HTMLDivElement>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { isDesktop, isTablet } = useMedia();
  const [NoDataFound, setNoDataFound] = useState(false);

  const initialArtId = getURLParameters('art');
  const initialItemId = getURLParameters('item');
  const [
    { loading, search, loadingParameter, items, componentData, selectedItem },
    { setSearch, setSelectedItem },
  ] = useSearch(initialArtId as string, initialItemId as string);
  const [itemFamily, setItemFamily] = useState<SparePart[]>([]);

  const onChangeSearch =
    (searchTerm?: string) => (e: HTMLElementEvent<HTMLInputElement>) => {
      const rawSearch = searchTerm || e.target.value.trim();
      const newSearch = removeEmojis(rawSearch);
      setSearch(newSearch);
      setSelectedItem(undefined);
    };

  /* Fetch family parts when selecting an item */
  useEffect(() => {
    const searchFamily = async (id: string) => {
      const itemFamilyData = await sparePartService.searchSparePartsFamily(id);
      setItemFamily(itemFamilyData);
    };

    if (selectedItem) {
      setSearch(`${selectedItem.id}`);
      searchFamily(`${selectedItem.id}`);
    }
  }, [selectedItem]);

  /* Onboarding show/hide logic */
  useEffect(() => {
    const shouldShow =
      !selectedItem &&
      !search.length &&
      CartContext.state.items.size === 0 &&
      items.length === 0 &&
      componentData.length === 0;

    setShowOnboarding(shouldShow);
    setNoDataFound(
      search.length > 0 &&
        items.length === 0 &&
        componentData.length === 0 &&
        !shouldShow
    );
  }, [search, selectedItem, items, componentData, CartContext.state.items]);

  /* Auto-focus search input */
  useLayoutEffect(() => {
    if (refInput.current) {
      const input = refInput.current.querySelector('input');
      if (input) input.focus();
    }
  }, []);

  if (loadingParameter) {
    return (
      <Loading text={t('loading', { loading: t('spareParts.sparePart') })}>
        <LoadingBall />
      </Loading>
    );
  }

  return (
    <>
      {/* ---------------- ONBOARDING VIEW ---------------- */}
      {showOnboarding && (
        <FlexContainer>
          <StyledSearchField ref={refInput}>
            <SparePartsDescription>
              <TMarkUp value={t('spareParts.onboarding.description')} />
            </SparePartsDescription>

            {isTablet && (
              <OnTabletImage>
                <AspectRatioImage ratio="wide" src={greenDayImage} alt="" />
              </OnTabletImage>
            )}

            <StyledSearch
              id="search-field"
              placeholder={t('spareParts.search.searchPartName')}
              clearBtnText={t('spareParts.search.clearButtonText')}
              onChange={onChangeSearch()}
              submitBtnText={t('spareParts.search.search')}
            />
          </StyledSearchField>

          {isDesktop && (
            <OnDesktopImage>
              <AspectRatioImage ratio="wide" src={greenDayImage} alt="" />
            </OnDesktopImage>
          )}
        </FlexContainer>
      )}

      {/* ---------------- SEARCH MODE ---------------- */}
      {!showOnboarding && (
        <SearchModeContainer>
          <StyledSearchField ref={refInput}>
            <StyledSearch
              id="search-field"
              placeholder={t('spareParts.search.searchPartName')}
              clearBtnText={t('spareParts.search.clearButtonText')}
              onChange={onChangeSearch()}
              submitBtnText={t('spareParts.search.search')}
            />
          </StyledSearchField>
        </SearchModeContainer>
      )}

      {/* ---------------- SEARCH RESULTS ---------------- */}
      <div aria-live="polite" aria-atomic="true">
        {showOnboarding ? (
          <Onboarding />
        ) : (
          <DisplaySearchItemsWrapper>
            {selectedItem ? (
              <SelectedItem
                item={selectedItem as SparePart}
                itemFamily={itemFamily}
                onClickItem={setSelectedItem}
                onChangeSearch={onChangeSearch}
                setSearch={setSearch}
                onSetSelectedItem={setSelectedItem}
                refInput={refInput}
              />
            ) : NoDataFound ? (
              <p>{t('error.noDataFound')}</p>
            ) : (
              <DisplaySearchItems
                loading={loading}
                items={items}
                componentData={componentData}
                search={search}
                itemFamily={itemFamily}
                onClickItem={setSelectedItem}
                onChangeSearch={onChangeSearch}
                setSearch={setSearch}
                onSetSelectedItem={setSelectedItem}
                refInput={refInput}
              />
            )}
          </DisplaySearchItemsWrapper>
        )}
      </div>
    </>
  );
};

export default SearchResults;
